<template>
    <div>
        <CompassTooltip
            :content="$t.__(getExtensionOption(option).label)"
            placement="top"
            :delay="{ show: 0, hide: 1 }"
            :disabled="disabled"
        >
            <CompassIconButton
                :icon="getExtensionOption(option).icon"
                size="small"
                :disabled="disabled"
                ref="linkRichTextTrigger"
                :active="activeStyle(option) || showMenuLink"
                @click="showLinkMenu(editor.getAttributes('link').href)"
            />
        </CompassTooltip>
        <CompassPopover
            :title="$t.__('richtext.customoptions.LinkRichText.add_link')"
            custom-class="custom-popover-link"
            triggerRef="linkRichTextTrigger"
            :isVisible="showMenuLink"
            @close="hideLinkMenu"
        >
            <div class="link-form-container">
                <!-- TODO: Uncomment when the text link will be implemented RichText V2 -->
                <!-- <CompassFormItem
                    :label="$t.__('richtext.customoptions.LinkRichText.text')"
                    :disabled="disabled"
                    :required="true"
                    :error="errorTextLink"
                    class="link-form-item-text"
                >
                    <CompassInput
                        v-model="textLink"
                        :disabled="disabled"
                        :error="!!errorTextLink"
                    />
                </CompassFormItem> -->
                <CompassFormItem
                    :label="$t.__('richtext.customoptions.LinkRichText.link')"
                    :disabled="disabled"
                    required
                    :requiredLabel="$t.__('common.labels.required')"
                    :error="errorUrlLink"
                    class="link-form-item-link"
                >
                    <CompassInput
                        ref="refInputLink"
                        v-model="urlLink"
                        :disabled="disabled"
                        :error="!!errorUrlLink"
                    />
                </CompassFormItem>
                <CompassLink
                    v-if="urlLink"
                    :label="$t.__('richtext.customoptions.LinkRichText.test_link')"
                    size="small"
                    :link="normalizeURL(urlLink)"
                    target="_blank"
                    class="link-test"
                    :class="errorUrlLink ? 'link-test-error' : ''"
                >
                    <template slot="suffix">
                        <CompassIcon icon="external-link" size="small" />
                    </template>
                </CompassLink>
                <CompassCheckbox
                    model-value="targetLink"
                    :label="$t.__('richtext.customoptions.LinkRichText.open_new_tab')"
                    :checked="targetLink"
                    show-label
                    :disabled="disabled"
                    class="link-target"
                    @change="targetLink = !targetLink"
                />
                <div class="link-container-action">
                    <CompassButton
                        :label="$t.__('richtext.customoptions.LinkRichText.delete_link')"
                        type="tertiary"
                        size="small"
                        :disabled="!urlLink"
                        @click="unUrlLink"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        size="small"
                        @click="hideLinkMenu"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.validate')"
                        size="small"
                        @click="setUrlLink(urlLink)"
                    />
                </div>
            </div>
        </CompassPopover>
    </div>
</template>

<script>
import OptionsRichTextMixin from 'mixins/OptionsRichTextMixin';

export default {
    name: 'LinkRichText',
    mixins: [OptionsRichTextMixin],
    data() {
        return {
            urlLink: '',
            textLink: '',
            emptyTextLink: true,
            showMenuLink: false,
            targetLink: true,
            errorUrlLink: '',
            errorUrlLinkMessage: this.$l10n.__('Le lien saisi est incorrect ou vide'),
            errorTextLink: '',
            errorTextLinkMessage: this.$l10n.__('Le texte saisi est vide'),
        };
    },
    watch: {
        urlLink() {
            // Reset the error message
            if (this.errorUrlLink) {
                this.errorUrlLink = '';
            }
            if (this.emptyTextLink) {
                // Set the textLink with the urlLink if the textLink is empty
                this.textLink = this.urlLink;
            }
        },
        textLink() {
            // Reset the error message
            if (this.errorTextLink) {
                this.errorTextLink = '';
            }
            if (this.textLink !== this.urlLink) {
                // Set the emptyTextLink to false if the textLink is different from the urlLink
                this.emptyTextLink = false;
            }
        },
    },
    methods: {
        // Get the selected text in the editor
        getSelectedText() {
            const { from, to, empty } = this.editor.state.selection;
            return empty ? '' : this.editor.state.doc.textBetween(from, to, ' ');
        },
        // Normalize the url (add https:// if not present)
        normalizeURL(url) {
            if (!url) return '';
            if (/^https?:\/\//i.test(url)) {
                return url;
            } else {
                return `https://${url}`;
            }
        },
        // Check if the url is valid
        checkValidUrl(url) {
            if (!url) return false;
            const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i;
            return !!urlPattern.test(url);
        },
        // Set the url link
        setUrlLink(url) {
            // Check if the url is valid
            if (!this.checkValidUrl(url)) {
                this.errorUrlLink = this.errorUrlLinkMessage;
                return;
            } else if (this.textLink === '') {
                this.errorTextLink = this.errorTextLinkMessage;
                return;
            }

            // Set the target
            const target = this.targetLink ? '_blank' : '_self';

            // Insert the link
            if (this.textLink && !this.getSelectedText()) {
                // Insert the link with the text
                this.editor.commands.insertContent(
                    `<a target="${target}" href="${this.normalizeURL(url)}">${this.textLink}</a> `,
                );
            } else {
                // Insert the link with the url
                this.editor.commands.setLink({ href: this.normalizeURL(url), target: target });
            }
            this.hideLinkMenu();
        },
        // Unset the url link
        unUrlLink() {
            this.urlLink = '';
            this.editor.commands.unsetLink();
        },
        // Show the link menu
        showLinkMenu(href) {
            if (this.showMenuLink) {
                this.hideLinkMenu();
                return;
            }
            this.textLink = this.getSelectedText();
            this.urlLink = this.textLink ? this.normalizeURL(href) : '';
            this.showMenuLink = true;
            // Set the focus on the input (need setTimeout because the popover is not yet visible)
            setTimeout(() => {
                this.$refs.refInputLink.setFocus();
            }, 0);
        },
        // Hide the link menu
        hideLinkMenu() {
            this.showMenuLink = false;
            this.$emit('close');
            this.resetValues();
        },
        // Reset the values
        resetValues() {
            this.textLink = '';
            this.urlLink = '';
            this.errorUrlLink = '';
            this.errorTextLink = '';
        },
    },
};
</script>

<style lang="scss">
.custom-popover-link {
    width: 320px;

    .link-form-item-text {
        margin: 0 0 var(--spacing-1);
    }
    .link-form-item-link {
        margin: 0 0 var(--spacing-0);
    }
    .link-test {
        display: flex;
        margin: 0 0 var(--spacing-1);
        justify-content: flex-end;
        &-error {
            margin: -20px 0 var(--spacing-1);
        }
    }
    .link-target {
        margin: 0 0 var(--spacing-2);
    }
    .link-container-action {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }
}
</style>
